/**
 * Reducer for the Common Balance Pool builder — the package's `ACTIONS = Object.freeze({...TPEAction})`
 * + `switch(action.type)` convention (cf. TPAllocationReducer / CalculationBuilderReducer).
 */
import TPEAction from 'src/models/common/TPEAction';
import { CommonBalancePoolBuilderState, getInitialState } from './CommonBalancePoolBuilderState';

export const ACTIONS = Object.freeze({
    RESET: new TPEAction('RESET'),
    SET_POOL: new TPEAction('SET_POOL'),
    SET_LOADING: new TPEAction('SET_LOADING'),
    SET_ERROR: new TPEAction('SET_ERROR'),
    SET_POOL_NEEDS_REFRESH: new TPEAction('SET_POOL_NEEDS_REFRESH'),
    SET_SAVING: new TPEAction('SET_SAVING'),
    SET_SAVE_ERROR: new TPEAction('SET_SAVE_ERROR'),
    SET_STEP2_DIRTY: new TPEAction('SET_STEP2_DIRTY'),
    OPEN_INPUT_MODAL: new TPEAction('OPEN_INPUT_MODAL'),
    CLOSE_INPUT_MODAL: new TPEAction('CLOSE_INPUT_MODAL'),
    SET_PERIOD: new TPEAction('SET_PERIOD'),
    SET_ACTION_BUSY: new TPEAction('SET_ACTION_BUSY'),
    SET_ACTION_ERROR: new TPEAction('SET_ACTION_ERROR'),
    SET_ACTION_NOTICE: new TPEAction('SET_ACTION_NOTICE'),
    SET_SEGMENT_TABLES: new TPEAction('SET_SEGMENT_TABLES'),
    RESULTS_RESET: new TPEAction('RESULTS_RESET'),
    RESULTS_LOADING: new TPEAction('RESULTS_LOADING'),
    RESULTS_LOADED: new TPEAction('RESULTS_LOADED'),
    RESULTS_ERROR: new TPEAction('RESULTS_ERROR'),
    RESULTS_DOWNLOAD_START: new TPEAction('RESULTS_DOWNLOAD_START'),
    RESULTS_DOWNLOAD_END: new TPEAction('RESULTS_DOWNLOAD_END'),
    RESULTS_REFRESH: new TPEAction('RESULTS_REFRESH'),
    SET_EXECUTION_STATUS: new TPEAction('SET_EXECUTION_STATUS'),
    SET_EXECUTION_TIMED_OUT: new TPEAction('SET_EXECUTION_TIMED_OUT'),
});

export function commonBalancePoolBuilderReducer(
    state: CommonBalancePoolBuilderState,
    action: TPEAction,
): CommonBalancePoolBuilderState {
    switch (action.type) {
        case ACTIONS.RESET.type:
            return getInitialState();
        case ACTIONS.SET_POOL.type:
            return { ...state, pool: action.payload, poolNeedsRefresh: false, isLoading: false };
        case ACTIONS.SET_LOADING.type:
            return { ...state, isLoading: action.payload };
        case ACTIONS.SET_ERROR.type:
            return { ...state, error: action.payload, isLoading: false };
        case ACTIONS.SET_POOL_NEEDS_REFRESH.type:
            return { ...state, poolNeedsRefresh: action.payload };
        case ACTIONS.SET_SAVING.type:
            return { ...state, saving: action.payload };
        case ACTIONS.SET_SAVE_ERROR.type:
            return { ...state, saveError: action.payload };
        case ACTIONS.SET_STEP2_DIRTY.type:
            return { ...state, step2Dirty: action.payload };
        case ACTIONS.OPEN_INPUT_MODAL.type:
            // payload = the InputRow to edit, or undefined to add a new input.
            return { ...state, addModalVisible: true, editingRow: action.payload, saveError: undefined };
        case ACTIONS.CLOSE_INPUT_MODAL.type:
            return { ...state, addModalVisible: false, editingRow: undefined };
        case ACTIONS.SET_PERIOD.type:
            // A run's status is per (pool, period); drop the prior period's execution status on a switch.
            // (Recovering a selected period's last status on switch/reload is the deferred backend read.)
            return { ...state, period: action.payload, executionStatus: undefined, executionTimedOut: false };
        case ACTIONS.SET_ACTION_BUSY.type:
            return { ...state, actionBusy: action.payload };
        case ACTIONS.SET_ACTION_ERROR.type:
            return { ...state, actionError: action.payload };
        case ACTIONS.SET_ACTION_NOTICE.type:
            return { ...state, actionNotice: action.payload };
        case ACTIONS.SET_SEGMENT_TABLES.type:
            // payload = { tables?, loading, error }. Keep the last-good list while a refetch is in flight.
            return {
                ...state,
                segmentTables: action.payload.tables ?? state.segmentTables,
                segmentTablesLoading: action.payload.loading,
                segmentTablesError: action.payload.error,
            };
        case ACTIONS.RESULTS_RESET.type:
            // Clear the Step-3 slice, e.g. before reloading the first page on a period/pool change.
            return {
                ...state,
                resultsRows: [],
                resultsNextToken: undefined,
                resultsTotalRowCount: undefined,
                resultsError: undefined,
                resultsLoading: false,
            };
        case ACTIONS.RESULTS_LOADING.type:
            return { ...state, resultsLoading: true, resultsError: undefined };
        case ACTIONS.RESULTS_LOADED.type: {
            // payload = { rows, nextToken, totalRowCount, append }. append concatenates the next page.
            const { rows, nextToken, totalRowCount, append } = action.payload;
            return {
                ...state,
                resultsRows: append ? [...state.resultsRows, ...(rows ?? [])] : (rows ?? []),
                resultsNextToken: nextToken,
                resultsTotalRowCount: totalRowCount,
                resultsLoading: false,
            };
        }
        case ACTIONS.RESULTS_ERROR.type:
            // payload = the message, or undefined to just clear the error (e.g. Alert dismiss).
            return { ...state, resultsError: action.payload, resultsLoading: false };
        case ACTIONS.RESULTS_DOWNLOAD_START.type:
            return { ...state, resultsDownloading: true, resultsDownloadError: undefined };
        case ACTIONS.RESULTS_DOWNLOAD_END.type:
            // payload = error message on failure, or undefined on success / dismiss.
            return { ...state, resultsDownloading: false, resultsDownloadError: action.payload };
        case ACTIONS.RESULTS_REFRESH.type:
            // Force the Step-3 preview to reset + refetch the selected period (e.g. after an Execute).
            return { ...state, resultsRefreshToken: state.resultsRefreshToken + 1 };
        case ACTIONS.SET_EXECUTION_STATUS.type:
            // payload = the polled CommonBalancePoolExecutionStatus, or undefined to clear. A fresh status
            // also clears any prior client-side poll timeout.
            return { ...state, executionStatus: action.payload, executionTimedOut: false };
        case ACTIONS.SET_EXECUTION_TIMED_OUT.type:
            // The 30-min client-side poll timeout fired (LLD §12.4); the run may still be going server-side.
            return { ...state, executionTimedOut: action.payload !== false };
        default:
            // eslint-disable-next-line no-console
            console.warn(`No action found for ${action.type}. Returning unchanged state`);
            return state;
    }
}
